/**
 * CoverArtSection — redistributed from coverClub/CoverDetailModal.jsx:
 * cover art, style/mood/palette/era tags, reactions, and the comment
 * thread, now folded into Book Details instead of a separate modal. Renders
 * nothing when this book has no Cover Club entry — most books won't.
 * Shelf actions/share are intentionally NOT duplicated here; they live once
 * in BookActionsPanel.
 */

import React from 'react';
import { cx } from '@/components/site/primitives';
import { useCoverClub } from '@/components/coverClub/context/CoverClubContext';
import CoverArt from '@/components/coverClub/CoverArt';
import ReactionBar from '@/components/coverClub/ReactionBar';
import CoverArtCommentThread from './CoverArtCommentThread';

const TONE_GLOW = { coral: 'bg-coral', yellow: 'bg-yellow', sage: 'bg-sage', blue: 'bg-blue' };

export default function CoverArtSection({ bookId }) {
  const { getEntry, getReactionCounts, myReactions, toggleReaction } = useCoverClub();
  const entry = getEntry(bookId);
  if (!entry) return null;

  return (
    <section className="u-container mt-10 md:mt-14" data-testid="book-details-cover-art">
      <div className="card-paper p-5 sm:p-6">
        <h2 className="font-display text-lg font-semibold text-ink">Cover Art</h2>
        <div className="mt-4 grid gap-5 sm:grid-cols-[160px_1fr]">
          <div className="relative mx-auto sm:mx-0">
            <span
              aria-hidden
              className={cx('pointer-events-none absolute -inset-4 -z-10 rounded-full blur-2xl opacity-40', TONE_GLOW[entry.book.tone] ?? TONE_GLOW.coral)}
            />
            <CoverArt book={entry.book} style={entry.style} className="h-52 w-36 rounded-xl shadow-lift" />
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap gap-1.5">
              {[entry.style, entry.mood, entry.palette, entry.era].filter(Boolean).map((tag) => (
                <span key={tag} className="rounded-full bg-cream px-2.5 py-0.5 text-[11px] font-medium text-subtle">{tag}</span>
              ))}
            </div>

            <div className="mt-3">
              <ReactionBar counts={getReactionCounts(entry)} myReaction={myReactions[bookId]} onToggle={(key) => toggleReaction(bookId, key)} />
            </div>

            <CoverArtCommentThread bookId={bookId} />
          </div>
        </div>
      </div>
    </section>
  );
}
